import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertVariant } from 'react-unity';
import { AlertBanner } from '../../../models/interfaces/AlertBanner';
import { AbstractForm, AbstractFormState, IStateForm } from '../../common/AbstractForm';
import { LinkTreeItem } from '../../common/LinkTree';
import FormModel from '../../../utils/FormModel';
import OnPremSiteToSiteVPNConfiguration from '../../../models/entities/OnPremSiteToSiteVPNConfiguration';
import OnPremSiteToSiteVPNStandardConfiguration from '../../../models/entities/OnPremSiteToSiteVPNStandardConfiguration';
import OnPremPORSiteToSiteVPNService from '../../../services/OnPremPORSiteToSiteVPNService';
import OnPremPortOpeningRequestService from '../../../services/OnPremPortOpeningRequestService';
import { authenticatedUser } from '../../../authentication/authModule';
import PortOpeningRequestSiteToSiteOnPrem from '../../../models/entities/PortOpeningRequestSiteToSiteOnPrem';
import EnvironmentConfiguration from '../../../utils/EnvironmentConfiguration';

interface EndModal extends AlertBanner {
    redirectionLink?: string;
}

export interface ISiteToSiteVPNStateForm extends IStateForm {
    id: number;
    type: string;
    companyName: string;
    trackerID: number;
    //workID: number;
    approver: string;
    exxonConfiguration: OnPremSiteToSiteVPNConfiguration;
    externalConfiguration: OnPremSiteToSiteVPNConfiguration;
    standardConfiguration: OnPremSiteToSiteVPNStandardConfiguration;
    exxonEncrypDomain: string;
    externalEncrypDomain: string;
    conDeviceCount: string;
    changeTicketId?: string;
}

export interface AbstractSiteToSiteVPNFormState<T> extends AbstractFormState<T> {
    operationInProgress: boolean;
    endModal: EndModal;
    submissionWarned: boolean;
    submissionAlert: AlertBanner;
    standardConfigurationData: any;
    loadingTracker?: boolean;
    failedTracker?: boolean,
    errorTracker?: string;
    approverOptions?: any[];
}

export const useAbstractSiteToSiteVPNForm = () => {
    const [state, setState] = useState<AbstractSiteToSiteVPNFormState<ISiteToSiteVPNStateForm>>({
        stateForm: {
            id: null,
            type: null,
            companyName: null,
            trackerID: null,
            //workID: null,
            approver: null,
            exxonConfiguration: null,
            externalConfiguration: null,
            standardConfiguration: null,
            exxonEncrypDomain: null,
            externalEncrypDomain: null,
            conDeviceCount: null,
            changeTicketId: null
        },
        operationInProgress: false,
        endModal: { visible: false },
        submissionWarned: false,
        submissionAlert: { visible: false },
        standardConfigurationData: null,
        approverOptions: []
    });

    const portOpeningRequestServiceOnPrem = new OnPremPortOpeningRequestService();
    const OnPremS2SVPNService = new OnPremPORSiteToSiteVPNService();
    const [lastTrackerId, setLastTrackerId] = useState<number | null>(null);

    const abstractFormInstance = new (class extends AbstractForm<{}, AbstractSiteToSiteVPNFormState<ISiteToSiteVPNStateForm>> {
        constructor() {
            super({});
            this.state = state;
        }
    })();

    const [formModel] = useState<FormModel>(
        abstractFormInstance.formModel = new FormModel({
            type: {
                getValue: () => state.stateForm.type,
                validation: {
                    required: true,
                },
            },
            companyName: {
                getValue: () => state.stateForm.companyName,
                validation: {
                    required: true,
                },
            },
            trackerID: {
                getValue: () => state.stateForm.trackerID,
                validation: {
                    required: true,
                    rules:
                        [
                            {
                                assert: () => /^[0-9]*$/.test(state.stateForm.trackerID?.toString() ?? ''),
                                message: 'Only numbers are valid values'
                            },
                            {
                                assert: () => state.stateForm.trackerID >= 10000,
                                message: 'Tracker ID must be grater than 10000'
                            }
                        ]
                },
            },
            /* workID: {
                getValue: () => state.stateForm.workID,
                validation: {
                    required: true,
                },
            }, */
            approver: {
                getValue: () => state.stateForm.approver,
                validation: {
                    required: false,
                },
            },
            exxonConfiguration: {
                getValue: () => state.stateForm.exxonConfiguration,
                validation: {
                    required: true,
                },
            },
            externalConfiguration: {
                getValue: () => state.stateForm.externalConfiguration,
                validation: {
                    required: true,
                },
            },
            standardConfiguration: {
                getValue: () => state.stateForm.standardConfiguration,
                validation: {
                    required: true,
                },
            },
            exxonEncrypDomain: {
                getValue: () => state.stateForm.exxonEncrypDomain,
                validation: {
                    required: true,
                },
            },
            externalEncrypDomain: {
                getValue: () => state.stateForm.externalEncrypDomain,
                validation: {
                    required: true,
                },
            },
            conDeviceCount: {
                getValue: () => state.stateForm.conDeviceCount,
                validation: {
                    required: true,
                },
            },
            changeTicketId: {
                getValue: () => state.stateForm.changeTicketId,
                validation: {
                    required: false,
                },
            },
        }));

    const navigate = useNavigate();

    const getLinkTree = (): LinkTreeItem[] => {
        return [
            { text: 'Site To Site VPN Requests', to: '/onPremSiteToSiteVPN' },
        ];
    };

    const handleStateFormChange = (field: string, value: any) => {
        abstractFormInstance.setState = (newState) => setState((prevState) => ({ ...prevState, ...newState }));
        abstractFormInstance.handleStateFormChange(field, value);
    };

    const handleStandardConfigData = async () => {
        try {
            const data = await OnPremS2SVPNService.getStandardConfigurationOptions();

            const transformed = {};
            Object.keys(data).forEach(field => {
                if (Array.isArray(data[field])) {
                    transformed[field] = data[field].map(item => ({
                        option: `Option${item.definedOption.item1}`,
                        availableOptions: item.definedOption.item2
                    }));
                }
            });

            setState(prevState => ({
                ...prevState,
                standardConfigurationData: transformed
            }));
        } catch (error) {
            setState(prevState => ({
                ...prevState,
                standardConfigurationData: null
            }));
        }
    }

    //const stateFormHandler = () => getStateFormHandlers();

    const getStateFormHandlers = () => {
        return {
            type: {
                value: state.stateForm.type,
                validation: formModel.fields.type?.validation,
                onChange: (event) => {
                    handleStateFormChange('type', event.target.value);
                },
            },
            companyName: {
                value: state.stateForm.companyName,
                validation: formModel.fields.companyName?.validation,
                onChange: (event) => {
                    handleStateFormChange('companyName', event.target.value);
                },
            },
            trackerID: {
                value: state.stateForm.trackerID,
                validation: formModel.fields.trackerID?.validation,
                onChange: (event) => {
                    if (event.target.value.length <= 5) {
                        handleStateFormChange('trackerID', event.target.value);
                        if (!(formModel.fields.trackerID.validation.rules.filter(x => x.assert() === false).length > 0) && !state.loadingTracker)
                            handleApproverDelegateOptions(state.stateForm.trackerID)
                    }
                    setState(prev => ({ ...prev, failedTracker: false }));
                },
            },
            /* workID: {
                value: state.stateForm.workID,
                validation: formModel.fields.workID?.validation,
                onChange: (event) => {
                    handleStateFormChange('workID', event.target.value);
                },
            }, */
            approver: {
                value: state.stateForm.approver,
                validation: formModel.fields.approver?.validation,
                onChange: (event) => {
                    handleStateFormChange('approver', event.target.value);
                },
                options: state.approverOptions ?? [],
            },
            exxonConfiguration: {
                exxonConfig: state.stateForm.exxonConfiguration,
                validation: formModel.fields.exxonConfiguration?.validation,
                onChange: (config: OnPremSiteToSiteVPNConfiguration) => {
                    handleStateFormChange('exxonConfiguration', config);
                },
            },
            externalConfiguration: {
                externalConfig: state.stateForm.externalConfiguration,
                validation: formModel.fields.externalConfiguration?.validation,
                onChange: (config: OnPremSiteToSiteVPNConfiguration) => {
                    handleStateFormChange('externalConfiguration', config);
                },
            },
            standardConfiguration: {
                standardConfig: state.stateForm.standardConfiguration,
                validation: formModel.fields.standardConfiguration?.validation,
                onChange: (config => {
                    handleStateFormChange('standardConfiguration', config)
                })
            },
            exxonEncrypDomain: {
                value: state.stateForm.exxonEncrypDomain,
                validation: formModel.fields.exxonEncrypDomain?.validation,
                onChange: (event => {
                    handleStateFormChange('exxonEncrypDomain', event.target.value)
                })
            },
            externalEncrypDomain: {
                value: state.stateForm.externalEncrypDomain,
                validation: formModel.fields.externalEncrypDomain?.validation,
                onChange: (event => {
                    handleStateFormChange('externalEncrypDomain', event.target.value)
                })
            },
            conDeviceCount: {
                value: state.stateForm.conDeviceCount,
                validation: formModel.fields.conDeviceCount?.validation,
                onChange: (event => {
                    handleStateFormChange('conDeviceCount', event.target.value)
                })
            },
            changeTicketId: {
            value: state.stateForm.changeTicketId,
            validation: formModel.fields.changeTicketId?.validation,
            onChange: (event) => {
                handleStateFormChange('changeTicketId', event.target.value);
            },
        },
        };
    };

    useEffect(() => {
        formModel.fields.type.getValue = () => state.stateForm.type;
        formModel.fields.companyName.getValue = () => state.stateForm.companyName;
        formModel.fields.trackerID.getValue = () => state.stateForm.trackerID;
        //formModel.fields.workID.getValue = () => state.stateForm.workID;
        formModel.fields.approver.getValue = () => state.stateForm.approver;
        formModel.fields.exxonConfiguration.getValue = () => state.stateForm.exxonConfiguration;
        formModel.fields.externalConfiguration.getValue = () => state.stateForm.externalConfiguration;
        formModel.fields.standardConfiguration.getValue = () => state.stateForm.standardConfiguration;
        formModel.fields.exxonEncrypDomain.getValue = () => state.stateForm.exxonEncrypDomain;
        formModel.fields.externalEncrypDomain.getValue = () => state.stateForm.externalEncrypDomain;
        formModel.fields.conDeviceCount.getValue = () => state.stateForm.conDeviceCount
    }, [state.stateForm]);

    const handleApproverDelegateOptions = async (trackerId: number) => {
        if (trackerId === lastTrackerId)
            return;

        try {
            setState(prev => ({ ...prev, loadingTracker: true }));

            const networkConnection = await portOpeningRequestServiceOnPrem.getNCDBbyTrackerId(trackerId);
            setLastTrackerId(trackerId);
            const options = [];

            if (EnvironmentConfiguration.isDev()) {
                options.push({ value: '56DBF9D0-44BB-42C0-B95B-72796CA91CE4', text: 'Martinelli, Natasha' })
                options.push({ value: 'b8032420-1914-4d03-8e27-fa61df630d63', text: 'Longin, Fernando Jose' })
                options.push({ value: 'cb6cf014-cb6f-4eac-95eb-4824174c972d', text: 'Orellana, Martin' })
                options.push({ value: 'e9eb8912-7c8f-4765-ac9d-83cbc3b3e2f4', text: 'Gemelli De Sousa, Eduardo Jose' })
                options.push({ value: '5f35b0ef-c43a-4dd4-b39a-37caf241dbdf', text: 'Walter, Facundo' })
                options.push({ value: '7a8d5c85-4bf3-468d-9bb6-8ff6bb51dc8a', text: 'Sukhaidi, Izzah' })
                options.push({ value: 'd394914c-79cb-4cde-840c-b7bd3f01db53', text: 'Kamil, Hasrul Akmal' })
                options.push({ value: '51d1e816-746f-45be-8a07-cf1d00508b30', text: 'Hussain, Nazri' })
                options.push({ value: 'd8d9bf25-c8ce-4527-b93f-31f7ed1f32ee', text: 'Montiel Fernandez, Ricardo Jose' })
                options.push({ value: '2a4a3ef2-099b-4b79-87ec-e6f2c18e9e59', text: 'Ramon, Emilio Hugo' })
                options.push({ value: 'abb6de11-8046-4593-89d1-5edb27fe4340', text: 'Alegre Saban, Leandro Nicolas' })
                options.push({ value: '4d4c12fe-8cd5-4fce-8d2e-9df3c28a9436', text: 'Post, Jeff' })
                options.push({ value: '56dbf9d0-44bb-42c0-b95b-72796ca91ce4', text: 'Martinelli, Natasha N' })
                options.push({ value: 'D600F7B8-08A9-4AD0-A2FC-58C6640D5A4B', text: 'Lagana, Claudia'})
            }
            else {
                if (networkConnection.owner?.id != null && networkConnection.owner?.id != authenticatedUser.userId)
                    options.push({ value: networkConnection.owner.id, text: networkConnection.owner.displayName })

                networkConnection.delegate.filter(x => x.id != authenticatedUser.userId).forEach(element => {
                    options.push({ value: element.id, text: element.displayName })
                });
            }
            setState(prev => ({
                ...prev,
                approverOptions: options,
            }));
            handleStateFormChange('firewall', networkConnection.ncp);
            return options;
        }
        catch (err) {
            const HTTP_NOT_FOUND_ERROR_CODE = 404;
            if (err.status === HTTP_NOT_FOUND_ERROR_CODE) {
                setLastTrackerId(trackerId);
            }
            setState(prev => ({
                ...prev,
                failedTracker: true,
                errorTracker: err?.response?.data?.message ?? 'There was an error while retrieving Tracker Id',
            }));
        }
        finally {
            setState(prev => ({ ...prev, loadingTracker: false }));
        }
    }

    const handleEndModal = (text: string, variant: AlertVariant, timeout: number, link: string) => {
        setState((prevState) => ({
            ...prevState,
            endModal: {
                visible: true,
                text,
                variant,
                redirectionLink: link,
            },
        }));
    };

    const endAndRedirect = () => {
        const link = state.endModal.redirectionLink;
        setState((prevState) => ({
            ...prevState,
            endModal: { visible: false },
        }));
        navigate(link || '/');
    };

    const setSubmissionAlertInProgress = (message: string) => {
        window.scroll(0, 0);
        setState((prevState) => ({
            ...prevState,
            operationInProgress: true,
            submissionAlert: {
                visible: true,
                text: message,
            },
        }));
    };

    const setSubmissionAlertError = (message: string) => {
        setState((prevState) => ({
            ...prevState,
            submissionAlert: {
                visible: true,
                text: message,
                variant: 'error',
            },
        }));
    };

    const handleSubmissionAlertClose = () => {
        setState((prevState) => ({
            ...prevState,
            submissionAlert: {
                visible: false,
            },
        }));
    };

    const isValid = () => {
        return true;
    };

    return {
        state,
        formModel,
        setState,
        getLinkTree,
        handleStateFormChange,
        getStateFormHandlers,
        handleEndModal,
        endAndRedirect,
        setSubmissionAlertInProgress,
        setSubmissionAlertError,
        handleSubmissionAlertClose,
        isValid,
        handleStandardConfigData,
        handleApproverDelegateOptions
    };
};

